import { IbePriceModelCode } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import { Direction, FindFlightsRequest, RailTransportType } from '../../../bewotec/openapi'
import { handleError } from '../../../general/errorHandling'
import { COMFORTAI, IND, INDAI } from '../../../general/master-data'
import { useCruiseStore, useDetailsStore, useFlightStore, useRailStore, useUserStore } from '../../../store'
import { appHelper, mapAgencyForRequest } from '../../../store/app/helper'
import { convertToExpitecDirection, mapRailOption } from '../rail/helper'
import {
  IFetchRailOption,
  IFetchRailOptionByDirection,
  IFindRailTransportationParameters,
  RailDirection,
} from '../rail/interfaces'
import { flightHelper, message } from './helper.js'
import { IExpinetFlightTypeMap, IExpinetSelectedFlight } from './interfaces'

export const fetchFlights = async (priceModel: IbePriceModelCode) => {
  const userStore = useUserStore()
  const flightStore = useFlightStore()
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()

  reset()

  const paxConfig = detailsStore.paxConfig!
  const cruise = cruiseStore.cruise
  const agencyId = mapAgencyForRequest(userStore.agencyId)

  try {
    const flightsRequest: FindFlightsRequest = {
      cruiseId: cruise!.id,
      agencyId,
      priceModelCode: priceModel,
      adults: paxConfig.adults,
      juveniles: paxConfig.juveniles,
      children: paxConfig.children,
      babies: paxConfig.babies,
    }
    const bewotecFlights = await appHelper.bewotecMicroServiceApi().findFlights(flightsRequest)
    const aidaFlights = flightHelper.initFlights(bewotecFlights)

    flightHelper.sortFlightsByAirportName(aidaFlights)

    flightStore.aidaFlights = aidaFlights
  } catch (error) {
    handleError({ error, origin: 'fetchFlights' })
    return
  }
}

export const preselectFlight = () => {
  const detailsStore = useDetailsStore()
  const flightStore = useFlightStore()
  const cruiseStore = useCruiseStore()

  const mandatoryFlight: boolean = detailsStore.mandatoryFlightForPriceModel
  const aidaFlights = flightStore.aidaFlights

  if (mandatoryFlight && !!aidaFlights) {
    const flightConnection = flightHelper.chooseFlight(cruiseStore.route, aidaFlights)

    flightStore.selectedFlight = flightConnection
  } else {
    flightStore.selectedFlight = null
  }
}

export const selectFlight = (flight: IExpinetFlightTypeMap | undefined) => {
  const detailsStore = useDetailsStore()
  const flightStore = useFlightStore()

  const selectedFlight = flightStore.selectedFlight

  if (!flight) {
    flightStore.selectedFlight = null

    if (!!selectedFlight && detailsStore.mandatoryFlightForPriceModel) {
      flightStore.preselectFlight()
    }
    return
  }

  const flightConnection: IExpinetSelectedFlight = {
    id: flight.outboundFlight?.id ?? flight.inboundFlight?.id ?? 0,
    price: flight.price,
    direction: flight.direction,
    outboundFlight: flight.direction === Direction.R ? null : flight.outboundFlight,
    inboundFlight: flight.direction === Direction.O ? null : flight.inboundFlight,
  }

  flightStore.selectedFlight = flightConnection

  resetRailWithoutRailAdults()
  setInitialRNFDirection()
}

export const fetchRailOptions = async () => {
  const railStore = useRailStore()
  const userStore = useUserStore()
  const flightStore = useFlightStore()
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()

  const gateway = flightStore.getGateway
  const cruiseId = cruiseStore.cruise?.id

  if (!gateway) return

  if (!cruiseId) {
    flightStore.selectableRailOptions = { error: true }
    handleError({ error: new Error('Cruise ID is missing'), origin: 'flight - fetchRailOptions' })
    return
  }

  const findRailTransportationParameters: IFindRailTransportationParameters = {
    cruiseId,
    agencyId: userStore.agencyId,
    priceModel: detailsStore.priceModel!,
    paxConfig: detailsStore.paxConfig!,
    transportationType: RailTransportType.RNF,
    childrenOlderThanFourteen: flightStore.railAdults,
    gateway,
    origin: 'flight',
  }

  const railOptions = await railStore.findRailTransportation(findRailTransportationParameters)
  const availableRailOptionsByDirection: IFetchRailOptionByDirection = {}

  if (!!railOptions?.length) {
    for (const option of railOptions) {
      if (!availableRailOptionsByDirection[option.direction]) {
        availableRailOptionsByDirection[option.direction] = []
      }

      const mappedOption = mapRailOption(option)
      availableRailOptionsByDirection[option.direction]?.push(mappedOption)
    }

    for (const direction of Object.keys(availableRailOptionsByDirection)) {
      const availableRailOptions: IFetchRailOption[] = availableRailOptionsByDirection?.[direction as Direction] ?? []

      availableRailOptions?.sort((a, b) => (a.railClassCode < b.railClassCode ? -1 : 1))
    }

    flightStore.selectableRailOptions = availableRailOptionsByDirection
  } else {
    flightStore.selectableRailOptions = { error: true }
  }
}

export const selectRNFRailDirection = async (railDirection: RailDirection) => {
  const flightStore = useFlightStore()

  if (flightStore.railDirectionRNF === railDirection) return

  flightStore.railDirectionRNF = railDirection
  flightStore.selectDefaultRailOption()
}

export const selectRailOption = (optionId: string) => {
  const flightStore = useFlightStore()

  const railDirection = flightStore.railDirectionRNF

  if (railDirection === 'none') {
    resetSelectedRailOption()
    return
  }

  const direction = convertToExpitecDirection(railDirection)
  const selectableRailOptions = flightStore.selectableRailOptions
  const hasSelectableRailOptions = !!selectableRailOptions && !('error' in selectableRailOptions)
  const options = hasSelectableRailOptions ? selectableRailOptions[direction] : []

  if (!!options?.length) {
    for (const option of options) {
      if (option !== null && 'id' in option && option.id === optionId) {
        flightStore.selectedRailOption = option
        break
      }
    }
  }
}

export const setInitialRNFDirection = () => {
  const flightStore = useFlightStore()
  const selectableRailOptions = flightStore.selectableRailOptions

  if (flightStore.isNoRailAllowed || 'error' in selectableRailOptions) {
    flightStore.railDirectionRNF = 'none'
    return
  }

  switch (flightStore.selectedFlight?.direction) {
    case Direction.RT:
    case Direction.OJ:
      if (!!selectableRailOptions[Direction.RT]?.length) flightStore.railDirectionRNF = 'both'
      else if (!!selectableRailOptions[Direction.O]?.length) flightStore.railDirectionRNF = 'outbound'
      else if (!!selectableRailOptions[Direction.R]?.length) flightStore.railDirectionRNF = 'inbound'
      else flightStore.railDirectionRNF = 'none'
      break
    case Direction.O:
      if (!!selectableRailOptions[Direction.O]?.length) flightStore.railDirectionRNF = 'outbound'
      else flightStore.railDirectionRNF = 'none'
      break
    case Direction.R:
      if (!!selectableRailOptions[Direction.R]?.length) flightStore.railDirectionRNF = 'inbound'
      else flightStore.railDirectionRNF = 'none'
      break
    // needed, otherwise we will get an error when none of the above is enabled
    // e.g. when the user selects a trans cruise + rnf, but deselects all flights again
    default:
      flightStore.railDirectionRNF = 'none'
      break
  }
}

export const selectDefaultRailOption = () => {
  const detailsStore = useDetailsStore()
  const flightStore = useFlightStore()

  const priceModel = detailsStore.priceModel!
  const direction = convertToExpitecDirection(flightStore.railDirectionRNF)
  const selectableRailOptions = flightStore.selectableRailOptions
  const isError = 'error' in selectableRailOptions

  if (!isError && [COMFORTAI].includes(priceModel)) {
    const firstClass = selectableRailOptions[direction]?.find(option => option.railClassCode === '1')
    const firstAvailableDirection = Object.keys(selectableRailOptions)[0] as Direction
    const firstAvailableSelection = selectableRailOptions[firstAvailableDirection]?.find(
      option => option.railClassCode === '1'
    )

    flightStore.selectedRailOption = firstClass ?? firstAvailableSelection ?? null
    flightStore.selectedRailOptionId = firstClass?.id ?? firstAvailableSelection?.id ?? ''
  }
  if (!isError && [IND, INDAI].includes(priceModel)) {
    const secondClass = selectableRailOptions[direction]?.find(option => option.railClassCode === '2')
    const firstAvailableDirection = Object.keys(selectableRailOptions)[0] as Direction
    const firstAvailableSelection = selectableRailOptions[firstAvailableDirection]?.find(
      option => option.railClassCode === '1'
    )

    flightStore.selectedRailOption = secondClass ?? firstAvailableSelection ?? null
    flightStore.selectedRailOptionId = secondClass?.id ?? firstAvailableSelection?.id ?? ''
  }
}

export const reset = () => {
  const flightStore = useFlightStore()

  resetRail()

  flightStore.aidaFlights = null
  flightStore.selectedFlight = null
  flightStore.railDirectionRNF = 'none'
}

export const resetRail = () => {
  const flightStore = useFlightStore()

  resetRailWithoutRailAdults()

  flightStore.railAdults = 0
}

export const resetRailWithoutRailAdults = () => {
  const flightStore = useFlightStore()

  resetSelectedRailOption()

  flightStore.railErrorMessage = ''
  flightStore.selectableRailOptions = {}
}

export const resetSelectedRailOption = () => {
  const flightStore = useFlightStore()

  flightStore.selectedRailOptionId = ''
  flightStore.selectedRailOption = null
}

export const preselectRailOption = async () => {
  const flightStore = useFlightStore()

  await flightStore.fetchRailOptions().then(() => {
    setInitialRNFDirection()
    selectDefaultRailOption()
  })
}

export const flightMessage = (flightDirection: 'outbound' | 'inbound') => {
  const flightStore = useFlightStore()
  const detailsStore = useDetailsStore()

  return message(flightStore.aidaFlights, detailsStore.priceModel!, flightDirection)
}

export default {
  fetchFlights,
  preselectFlight,
  selectFlight,
  fetchRailOptions,
  selectRNFRailDirection,
  selectRailOption,
  setInitialRNFDirection,
  selectDefaultRailOption,
  resetRail,
  resetRailWithoutRailAdults,
  resetSelectedRailOption,
  preselectRailOption,
  flightMessage,
}

export const exportedForTesting = {
  reset,
}
